import { Head } from '@inertiajs/react'
import { useCallback, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import AppLayout from '@components/layout/Layout/AppLayout'
import Breadcrumb from '@components/layout/Breadcrumb'
import FileExplorerSidebar from '@features/file-explorer/components/FileExplorerSidebar'
import FileExplorerToolbar from '@features/file-explorer/components/FileExplorerToolbar'
import FileExplorerToolbarMobile from '@features/file-explorer/components/FileExplorerToolbarMobile'
import FileExplorerContent from '@features/file-explorer/components/FileExplorerContent'
import FileExplorerModals, { FileExplorerModalsHandle } from '@features/file-explorer/components/FileExplorerModals'
import { FileExplorerProps } from '@features/file-explorer/types'
import { useFileExplorerNavigation } from '@features/file-explorer/hooks/useFileExplorerNavigation'
import { useFileExplorerHandlers } from '@features/file-explorer/hooks/useFileExplorerHandlers'
import { useFileExplorerDragAndDrop } from '@features/file-operations/hooks/useFileExplorerDragAndDrop'
import { useDocumentSubscriptions } from '@features/file-explorer/hooks/useDocumentSubscriptions'
import { useNotificationContext } from '@contexts/NotificationContext'
import { usePersistedState } from '@hooks/usePersistedState'
import { getFilterFields } from '@features/file-explorer/config/filterFields'
import { useSelectionStore } from '@stores/useSelectionStore'

export default function Index({
  items,
  pagination,
  current_folder_id,
  breadcrumbs,
  sort_by,
  sort_direction,
  per_page,
  filters,
  folder_tree,
  storage_usage,
  user,
  errors
}: FileExplorerProps) {
  const { t } = useTranslation()
  const modalRef = useRef<FileExplorerModalsHandle>(null)

  // Navigation and loading state
  const {
    loadingElement,
    loadingFolderId,
    handleSortChange,
    handleDirectionToggle,
    handlePageChange,
    handleApplyFilters,
    handlePerPageChange,
    handleFolderNavigation,
    handleItemClick
  } = useFileExplorerNavigation({
    current_folder_id,
    sort_by,
    sort_direction,
    filters,
    page: pagination.page,
    per_page
  })

  const selectedCount = useSelectionStore(state => state.selectedItems.size)

  const { showToast, updateToast, dismissToast } = useNotificationContext()

  // Drag and drop state
  const dragAndDrop = useFileExplorerDragAndDrop({
    currentFolderId: current_folder_id,
    onShowToast: showToast,
    onUpdateToast: updateToast,
    onDismissToast: dismissToast,
    t
  })

  useDocumentSubscriptions({ items })

  const [isSidebarOpen, setIsSidebarOpen] = usePersistedState('fileExplorer.sidebarOpen', true)
  const [viewMode, setViewMode] = usePersistedState<'table' | 'grid'>('fileExplorer.viewMode', 'table')

  const filterFields = getFilterFields(t)

  const {
    getItemKey,
    handleSelectItem,
    handleSelectAll,
    handleClearSelection,
    handleDeleteClick,
    handleBulkDelete,
    handleRenameClick,
    handleDownload,
    handleBulkDownload,
    handleShow,
    handleManagePermissions
  } = useFileExplorerHandlers({
    items,
    currentFolderId: current_folder_id,
    modalRef,
    onItemClick: handleItemClick,
    showToast,
    t
  })

  const toggleSidebar = useCallback(() => {
    setIsSidebarOpen(prev => !prev)
  }, [])

  // Keyboard shortcut: Ctrl+A to select all
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Check for Ctrl+A (Windows/Linux) or Cmd+A (Mac)
      if ((e.ctrlKey || e.metaKey) && e.key === 'a') {
        // Ignore if user is typing in an input/textarea
        const target = e.target as HTMLElement
        if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable) {
          return
        }

        e.preventDefault()
        handleSelectAll()
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [handleSelectAll])

  // Auto-clean selection when items change (navigation, sorting, filtering)
  useEffect(() => {
    const validKeys = items.map(item => getItemKey(item))
    useSelectionStore.getState().cleanSelection(validKeys)
  }, [items, getItemKey])

  return (
    <AppLayout user={user}>
      <Head title={t('titles.fileExplorer')} />

      <div className="container mx-auto px-2 sm:px-4">
        {/* Breadcrumb Navigation */}
        <div className="p-2 sm:p-4">
          <Breadcrumb items={breadcrumbs} currentItemId={current_folder_id} />
        </div>

        {/* Main Layout: Tree Panel + Content */}
        <div className="flex gap-2 sm:gap-4 items-stretch">
          {/* Folder Tree Sidebar */}
          <FileExplorerSidebar
            tree={folder_tree}
            currentFolderId={current_folder_id}
            onNavigate={handleFolderNavigation}
            loadingFolderId={loadingFolderId}
            dragAndDrop={dragAndDrop}
            storageUsage={storage_usage}
            isOpen={isSidebarOpen}
            onToggle={toggleSidebar}
          />

          {/* Main Content Area */}
          <div className="flex-1 min-w-0">
            <div className="rounded-lg shadow-md">
              {/* Mobile Toolbar - Hidden on desktop */}
              <div className="sm:hidden">
                <FileExplorerToolbarMobile
                  selectedCount={selectedCount}
                  totalCount={items.length}
                  pagination={pagination}
                  sortBy={sort_by}
                  sortDirection={sort_direction}
                  filters={filters}
                  filterFields={filterFields}
                  viewMode={viewMode}
                  loadingElement={loadingElement}
                  isSidebarOpen={isSidebarOpen}
                  onToggleSidebar={toggleSidebar}
                  onClearSelection={handleClearSelection}
                  onSelectAll={handleSelectAll}
                  onBulkDownload={handleBulkDownload}
                  onBulkDelete={handleBulkDelete}
                  onSortChange={handleSortChange}
                  onDirectionToggle={handleDirectionToggle}
                  onApplyFilters={handleApplyFilters}
                  onViewChange={setViewMode}
                  onCreateFolder={() => modalRef.current?.openFolderModal()}
                  onUploadDocument={() => modalRef.current?.openUploadModal()}
                />
              </div>

              {/* Desktop Toolbar - Hidden on mobile */}
              <div className="hidden sm:block">
                <FileExplorerToolbar
                  selectedCount={selectedCount}
                  totalCount={items.length}
                  pagination={pagination}
                  sortBy={sort_by}
                  sortDirection={sort_direction}
                  filters={filters}
                  filterFields={filterFields}
                  viewMode={viewMode}
                  loadingElement={loadingElement}
                  isSidebarOpen={isSidebarOpen}
                  onToggleSidebar={toggleSidebar}
                  onClearSelection={handleClearSelection}
                  onSelectAll={handleSelectAll}
                  onBulkDownload={handleBulkDownload}
                  onBulkDelete={handleBulkDelete}
                  onSortChange={handleSortChange}
                  onDirectionToggle={handleDirectionToggle}
                  onApplyFilters={handleApplyFilters}
                  onViewChange={setViewMode}
                  onCreateFolder={() => modalRef.current?.openFolderModal()}
                  onUploadDocument={() => modalRef.current?.openUploadModal()}
                />
              </div>

              {/* Error Display */}
              {errors && Object.keys(errors).length > 0 && (
                <div className="alert alert-error mb-4">
                  <span>{JSON.stringify(errors)}</span>
                </div>
              )}

              {/* Content (Table/Grid + Pagination) */}
              <FileExplorerContent
                items={items}
                viewMode={viewMode}
                getItemKey={getItemKey}
                onItemClick={handleItemClick}
                onSelectItem={handleSelectItem}
                onDelete={handleDeleteClick}
                onRename={handleRenameClick}
                onDownload={handleDownload}
                onShow={handleShow}
                onManagePermissions={handleManagePermissions}
                isLoading={loadingElement !== null}
                dragAndDrop={dragAndDrop}
                pagination={pagination}
                perPage={per_page}
                onPageChange={handlePageChange}
                onPerPageChange={handlePerPageChange}
                loadingPrev={loadingElement === 'pagination-prev'}
                loadingNext={loadingElement === 'pagination-next'}
                loadingPerPage={loadingElement === 'per-page'}
              />
            </div>
          </div>
        </div>
      </div>

      {/* All Modals */}
      <FileExplorerModals
        ref={modalRef}
        currentFolderId={current_folder_id}
      />
    </AppLayout>
  )
}
